import { useEffect, useRef, useState } from "react";
import { Mic, Upload, Square, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

interface TalkingAvatarCloneModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the recorded/uploaded audio Blob + chosen metadata.
   *  Parent is responsible for upload + API call + toasts. */
  onSubmit: (params: { audio: Blob; filename: string; name: string; gender: "male" | "female" }) => Promise<void>;
  /** Which CTA opened the modal. "upload" auto-fires the native file picker
   *  on open so the user doesn't have to click again — they already chose
   *  "upload" upstream. "record" leaves the user on the standard dual-button
   *  layout. Default "record" preserves prior behavior. */
  initialAction?: "record" | "upload";
}

const MIN_RECOMMENDED_SECONDS = 30;

export function TalkingAvatarCloneModal({
  open,
  onOpenChange,
  onSubmit,
  initialAction = "record",
}: TalkingAvatarCloneModalProps) {
  const { t } = useLanguage();

  const [name, setName] = useState("");
  const [gender, setGender] = useState<"male" | "female">("female");
  const [audio, setAudio] = useState<{ blob: Blob; filename: string } | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // MediaRecorder lifecycle — refs so re-renders don't kill the recorder.
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** Ref to the file input so the "Upload existing voice" entry point on the
   *  voice picker can auto-fire the native file picker once the dialog has
   *  mounted (see the initialAction effect below). */
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // When the modal opens via the "upload" CTA, programmatically click the
  // file input on the next tick so the OS file picker pops immediately —
  // the user already chose "upload" upstream, no reason to make them click
  // again. Guarded by `open` so subsequent renders don't re-fire it.
  useEffect(() => {
    if (open && initialAction === "upload") {
      const t = setTimeout(() => fileInputRef.current?.click(), 50);
      return () => clearTimeout(t);
    }
  }, [open, initialAction]);

  // Reset everything when the modal closes.
  useEffect(() => {
    if (!open) {
      stopRecording(false);
      setName("");
      setGender("female");
      setAudio(null);
      setRecordingSeconds(0);
      setIsSubmitting(false);
    }
  }, [open]);

  function stopRecording(commit: boolean) {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      if (commit) {
        rec.stop();
      } else {
        // Discard — pause/stop tracks without consuming chunks.
        rec.stream.getTracks().forEach((t) => t.stop());
        rec.stop();
        chunksRef.current = [];
      }
    }
    recorderRef.current = null;
    setIsRecording(false);
  }

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const ext = (rec.mimeType.split("/")[1] ?? "webm").split(";")[0];
        setAudio({ blob, filename: `recording.${ext}` });
        stream.getTracks().forEach((t) => t.stop());
      };
      rec.start();
      recorderRef.current = rec;
      setIsRecording(true);
      setRecordingSeconds(0);
      tickerRef.current = setInterval(() => {
        setRecordingSeconds((s) => s + 1);
      }, 1000);
    } catch {
      // Likely permission denied. UI stays in "not recording" state.
    }
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAudio({ blob: file, filename: file.name });
    e.target.value = "";
  }

  async function submit() {
    if (!audio || !name.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit({ audio: audio.blob, filename: audio.filename, name: name.trim(), gender });
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  const canSubmit = !!audio && name.trim().length > 0 && !isRecording && !isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.talkingAvatar.cloneModalTitle}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t.talkingAvatar.cloneModalDescription}
        </p>

        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t.talkingAvatar.cloneModalNameLabel}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.talkingAvatar.cloneModalNamePlaceholder}
              maxLength={50}
              className="w-full rounded-xl border border-border bg-card px-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t.talkingAvatar.cloneModalGenderLabel}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(["female", "male"] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  aria-pressed={gender === g}
                  className={cn(
                    "px-3 py-2 rounded-xl border text-sm font-medium transition-colors",
                    gender === g
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border bg-card hover:bg-secondary text-foreground",
                  )}
                >
                  {g === "female" ? t.talkingAvatar.voiceGenderFemale : t.talkingAvatar.voiceGenderMale}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={isRecording ? () => stopRecording(true) : startRecording}
              className={cn(
                "inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-colors",
                isRecording
                  ? "border-destructive bg-destructive/10 text-destructive"
                  : "border-border bg-card hover:bg-secondary text-foreground",
              )}
            >
              {isRecording ? (
                <>
                  <Square className="w-4 h-4" />
                  {t.talkingAvatar.cloneModalStopCta}
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4" />
                  {t.talkingAvatar.cloneModalRecordCta}
                </>
              )}
            </button>
            <label className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-border bg-card hover:bg-secondary text-foreground text-sm font-medium cursor-pointer">
              <Upload className="w-4 h-4" />
              {t.talkingAvatar.cloneModalUploadCta}
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={handleFile}
                disabled={isRecording}
              />
            </label>
          </div>

          {(isRecording || audio) && (
            <p
              className={cn(
                "text-xs",
                isRecording && recordingSeconds < MIN_RECOMMENDED_SECONDS
                  ? "text-amber-600 dark:text-amber-400"
                  : "text-muted-foreground",
              )}
            >
              {isRecording
                ? t.talkingAvatar.cloneModalRecordingHint.replace("{seconds}", String(recordingSeconds))
                : audio
                  ? t.talkingAvatar.cloneModalUploadHint
                      .replace("{filename}", audio.filename)
                      .replace("{size}", String(Math.round(audio.blob.size / 1024)))
                  : ""}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-border/50">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-xl border border-border bg-card hover:bg-secondary text-sm font-medium text-foreground disabled:opacity-50"
            >
              {t.talkingAvatar.cloneModalCancel}
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r gradient-primary text-white text-sm font-bold transition-all",
                "shadow-[0_4px_12px_rgba(0,0,0,0.15)] disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting
                ? t.talkingAvatar.cloneModalCloning
                : t.talkingAvatar.cloneModalSubmit}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
